import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react'
import {
	Button,
	Dialog, DialogContent, DialogFooter, DialogHeader,
} from '@rentflowdev/ui'
import type { DocumentTemplate } from '@/types'

type TemplateGroupKey = 'contract' | 'transfer' | 'appendix'

const GROUP_ORDER: TemplateGroupKey[] = ['contract', 'transfer', 'appendix']

function classifyTemplate(title: string): TemplateGroupKey {
	if (title.startsWith('Акт')) return 'transfer'
	if (title.startsWith('Дополнительное соглашение')) return 'appendix'
	return 'contract'
}

interface SelectTemplateDialogProps {
	open: boolean
	templates: DocumentTemplate[]
	onOpenChange: (open: boolean) => void
	onConfirm: (template: DocumentTemplate) => void
}

export function SelectTemplateDialog({ open, templates, onOpenChange, onConfirm }: SelectTemplateDialogProps) {
	const { t } = useTranslation()
	const [selected, setSelected] = useState<DocumentTemplate | null>(null)
	const [search, setSearch]     = useState('')
	const [pickerOpen, setPickerOpen] = useState(false)
	const pickerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setSelected(null)
			setSearch('')
			setPickerOpen(false)
		}
	}, [open])

	// Close the picker popover when clicking outside of it (but inside the dialog).
	useEffect(() => {
		if (!pickerOpen) return
		function handleDocClick(e: MouseEvent) {
			if (!pickerRef.current) return
			if (e.target instanceof Node && !pickerRef.current.contains(e.target)) {
				setPickerOpen(false)
			}
		}
		document.addEventListener('mousedown', handleDocClick)
		return () => document.removeEventListener('mousedown', handleDocClick)
	}, [pickerOpen])

	const grouped = useMemo(() => {
		const q = search.trim().toLowerCase()
		const buckets: Record<TemplateGroupKey, DocumentTemplate[]> = {
			contract: [],
			transfer: [],
			appendix: [],
		}
		for (const tpl of templates) {
			if (q && !tpl.title.toLowerCase().includes(q)) continue
			const key = classifyTemplate(tpl.title)
			buckets[key].push(tpl)
		}
		return buckets
	}, [templates, search])

	function handlePick(tpl: DocumentTemplate) {
		setSelected(tpl)
		setPickerOpen(false)
	}

	function handleNext() {
		if (selected) onConfirm(selected)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col p-0 gap-0 overflow-visible rounded-(--radius-lg)"
				style={{ width: 480, maxWidth: '95vw' }}
			>
				<DialogHeader title={t('documents.selectDialog.title')} />

				<div className="px-6 py-4">
					<div className="flex flex-col gap-2">
						<label className="text-sm text-(--color-foreground)">
							{t('documents.selectDialog.templateLabel')}
						</label>

						<div className="relative" ref={pickerRef}>
							<button
								type="button"
								onClick={() => setPickerOpen((v) => !v)}
								className="flex h-10 w-full items-center justify-between rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-4 text-sm transition-colors hover:bg-(--color-muted)/40"
							>
								<span className={selected ? 'text-(--color-foreground)' : 'text-(--color-muted-foreground)'}>
									{selected?.title ?? t('documents.selectDialog.templatePlaceholder')}
								</span>
								<ChevronDownIcon className="size-4 text-(--color-muted-foreground)" />
							</button>

							{pickerOpen && (
								<div
									className="absolute left-0 right-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-popover) shadow-(--shadow-md)"
									style={{ maxHeight: 360 }}
								>
									<div className="flex shrink-0 items-center gap-2 border-b border-(--color-border) px-3 py-2.5">
										<SearchIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
										<input
											type="text"
											value={search}
											onChange={(e) => setSearch(e.target.value)}
											placeholder={t('documents.selectDialog.search')}
											className="flex-1 bg-transparent text-sm leading-5 text-(--color-foreground) placeholder:text-(--color-muted-foreground) focus:outline-none"
										/>
									</div>

									<div className="flex-1 overflow-y-auto">
										{GROUP_ORDER.map((groupKey) => {
											const items = grouped[groupKey]
											return (
												<div
													key={groupKey}
													className="flex flex-col items-start p-1 [&:not(:first-of-type)]:border-t [&:not(:first-of-type)]:border-(--color-border)"
												>
													<div className="px-2 py-1.5 text-sm font-[600] leading-5 text-(--color-foreground)">
														{t(`documents.selectDialog.groups.${groupKey}`)}
													</div>
													{items.map((tpl) => {
														const isSelected = selected?.id === tpl.id
														return (
															<button
																key={tpl.id}
																type="button"
																onClick={() => handlePick(tpl)}
																className={`relative flex w-full items-center gap-2 rounded-(--radius-sm) py-1.5 pl-8 pr-2 text-sm leading-5 text-left transition-colors hover:bg-(--color-accent) ${isSelected ? 'bg-(--color-accent) text-(--color-accent-foreground)' : 'text-(--color-popover-foreground)'}`}
															>
																{isSelected && (
																	<CheckIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-(--color-accent-foreground)" />
																)}
																<span className="flex-1 truncate">{tpl.title}</span>
															</button>
														)
													})}
												</div>
											)
										})}
									</div>

									<button
										type="button"
										onClick={() => setSearch('')}
										className="flex w-full shrink-0 items-center justify-center gap-2 border-t border-(--color-border) px-2 py-1.5 text-sm leading-5 text-(--color-popover-foreground) transition-colors hover:bg-(--color-accent)"
									>
										{t('documents.selectDialog.clearFilter')}
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
						{t('documents.selectDialog.back')}
					</Button>
					<Button type="button" onClick={handleNext} disabled={!selected}>
						{t('documents.selectDialog.next')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
